import axios from 'axios';
import { socket } from './SocketService';

const API_BASE_URL = '/api'; // Usar proxy do Vite

// Configurar axios com interceptors
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // ✅ 10 segundos (reduzido de 30s para não deixar usuário esperando)
  withCredentials: true, // Para cookies
  headers: {
    'Content-Type': 'application/json',
  }
});

// ✅ SEGURANÇA: Tokens agora em cookies httpOnly
// Navegador envia automaticamente, não precisa adicionar manualmente
// Manter withCredentials: true para enviar cookies
api.interceptors.request.use(
  (config) => {
    // Cookies são enviados automaticamente pelo navegador
    // Não precisa mais adicionar Authorization header manualmente
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Variável para controlar se já está renovando
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Interceptor para tratamento de respostas
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // ✅ RETRY AUTOMÁTICO APENAS PARA ERROS DE REDE (não credenciais)
    // Erros de rede: timeout, connection refused, DNS failure, etc.
    const isNetworkError = !error.response || 
                          error.code === 'ECONNABORTED' || 
                          error.code === 'ERR_NETWORK' ||
                          error.code === 'ETIMEDOUT';
    
    if (isNetworkError && !originalRequest._networkRetry) {
      originalRequest._networkRetryCount = originalRequest._networkRetryCount || 0;
      
      // Tentar até 3 vezes APENAS para erros de rede
      if (originalRequest._networkRetryCount < 3) {
        originalRequest._networkRetryCount++;
        originalRequest._networkRetry = true;
        
        console.log(`🔄 Retry ${originalRequest._networkRetryCount}/3 por erro de rede`);
        
        // Aguardar 2 segundos antes de tentar novamente
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        return api(originalRequest);
      }
    }
    
    // Se erro 401 e não é rota de refresh ou login
    // ✅ CRÍTICO: Não tentar renovar token em erro de LOGIN (senha errada)
    if (error.response?.status === 401 && !originalRequest._retry && 
        !originalRequest.url.includes('/refresh-token') && 
        !originalRequest.url.includes('/auth/login')) {
      
      if (isRefreshing) {
        // Já está renovando, adicionar à fila
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      // Refresh token está em cookie httpOnly, backend vai gerenciar
      try {
        // Tentar renovar token (cookies enviados automaticamente)
        const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {}, {
          withCredentials: true // Enviar cookies
        });
        
        // Backend já atualizou o cookie, apenas processar fila
        processQueue(null, true);
        
        // Retentar requisição original (cookie atualizado será enviado)
        return api(originalRequest);
        
      } catch (refreshError) {
        // ✅ SOLUÇÃO UNIFICADA: Emitir evento ao invés de redirecionar
        // Permite que SessionManager coordene o logout com modal
        processQueue(refreshError, null);
        
        // Emitir evento customizado
        window.dispatchEvent(new CustomEvent('session_expired', {
          detail: { 
            reason: 'token_refresh_failed',
            error: refreshError.response?.data?.message || 'Falha ao renovar sessão'
          }
        }));
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    // ✅ CORREÇÃO CRÍTICA: NÃO transformar em Error simples
    // Manter error.response para que componentes possam acessar status e data
    return Promise.reject(error);
  }
);

export class AuthService {
  // Login
  static async login(email, password) {
    try {
      const response = await api.post('/auth/login', { email, password });
      
      // ✅ SEGURANÇA: Tokens agora estão em cookies httpOnly (imunes a XSS)
      // Apenas salvar dados do usuário (não sensíveis) no localStorage
      if (response.data.success && response.data.data) {
        const { user } = response.data.data;
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('sessionStartTime', Date.now().toString());
        
        // Tokens NÃO são mais armazenados aqui!
        // Eles estão em cookies httpOnly gerenciados pelo navegador
        
        // ✅ Emitir evento Socket.IO para registrar usuário como online
        if (socket && socket.connected) {
          socket.emit('user_login', user);
        }
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Logout
  static async logout() {
    try {
      // Backend vai limpar os cookies httpOnly
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Erro no logout:', error);
    } finally {
      // Limpar apenas dados do usuário (tokens já foram limpos pelo backend)
      localStorage.removeItem('user');
      localStorage.removeItem('sessionStartTime');
    }
  }

  // Registrar usuário (apenas para admins)
  static async register(userData) {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Obter perfil do usuário atual
  static async getProfile() {
    try {
      const response = await api.get('/auth/profile');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Atualizar perfil
  static async updateProfile(profileData) {
    try {
      const response = await api.put('/auth/profile', profileData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Solicitar reset de senha
  static async requestPasswordReset(email) {
    try {
      const response = await api.post('/auth/request-password-reset', { email });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Reset de senha
  static async resetPassword(token, password) {
    try {
      const response = await api.post(`/auth/reset-password/${token}`, { password });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Alterar senha (usuário logado)
  static async changePassword(passwordData) {
    try {
      const response = await api.post('/auth/change-password', passwordData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Verificar email
  static async verifyEmail(token) {
    try {
      const response = await api.get(`/auth/verify-email/${token}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Verificar se usuário está logado
  static isAuthenticated() {
    // ✅ SEGURANÇA: Verificar apenas se há dados do usuário
    // Token está em cookie httpOnly, backend valida
    const user = localStorage.getItem('user');
    
    if (!user) {
      return false;
    }
    
    try {
      // Validar se dados do usuário são válidos
      JSON.parse(user);
      return true;
    } catch (error) {
      // Dados inválidos, limpar
      localStorage.removeItem('user');
      localStorage.removeItem('sessionStartTime');
      return false;
    }
  }

  // Obter usuário atual do localStorage
  static getCurrentUser() {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Erro ao obter usuário atual:', error);
      return null;
    }
  }

  // Verificar se usuário é admin
  static isAdmin() {
    const user = this.getCurrentUser();
    return user && (user.role === 'admin' || user.role === 'super_admin');
  }

  // Verificar se usuário é super admin
  static isSuperAdmin() {
    const user = this.getCurrentUser();
    return user && (user.role === 'super_admin' || user.is_super_admin);
  }

  // Verificar se pode gerenciar usuários
  static canManageUsers() {
    const user = this.getCurrentUser();
    // ✅ CORRIGIDO: Administradores também têm acesso
    return user && (this.isSuperAdmin() || user.role === 'admin' || user.can_manage_users);
  }

  // ✅ NOVO: Verificar se usuário é observador
  static isObserver() {
    const user = this.getCurrentUser();
    return user && user.role === 'observer';
  }

  // ✅ NOVO: Verificar se pode editar parcerias (não é observador)
  static canEditPartnerships() {
    return !this.isObserver();
  }

  // Renovar sessão manualmente
  static async refreshSession() {
    try {
      // Refresh token está em cookie httpOnly
      const response = await api.post('/auth/refresh-token', {});
      
      // Backend atualiza o cookie automaticamente
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Verificar expiração do token
  static async checkTokenExpiration() {
    try {
      const response = await api.get('/auth/check-expiration');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Logout de todos os dispositivos
  static async logoutAll() {
    try {
      await api.post('/auth/logout-all');
    } catch (error) {
      console.error('Erro no logout de todos os dispositivos:', error);
    } finally {
      // Limpar dados locais (cookies limpos pelo backend)
      localStorage.removeItem('user');
      localStorage.removeItem('sessionStartTime');
    }
  }

  // Reenviar email de verificação
  static async resendVerificationEmail(userId) {
    try {
      const response = await api.post(`/auth/resend-verification/${userId}`);
      return response.data;
    } catch (error) {
      // Extrair mensagem de erro do backend
      const message = error.response?.data?.message || error.message || 'Erro ao reenviar email de verificação';
      throw new Error(message);
    }
  }
}

export class UserService {
  // Listar usuários
  static async list(params = {}) {
    try {
      const response = await api.get('/users', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Obter usuário por ID
  static async getById(id) {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Atualizar usuário
  static async update(id, userData) {
    try {
      const response = await api.put(`/users/${id}`, userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Deletar usuário
  static async delete(id) {
    try {
      const response = await api.delete(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Ativar/Desativar usuário
  static async toggleStatus(id) {
    try {
      const response = await api.patch(`/users/${id}/toggle-status`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Conceder/Revogar permissão de gerenciamento
  static async toggleUserManagement(id) {
    try {
      const response = await api.patch(`/users/${id}/toggle-user-management`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Obter estatísticas de usuários
  static async getStats() {
    try {
      const response = await api.get('/users/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Restaurar usuário deletado
  static async restore(id) {
    try {
      const response = await api.post(`/users/${id}/restore`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export default AuthService;
